import React, { useMemo } from 'react';
import registry, { getBlockComponent } from './registry';
import { EditableSection, AddBlockButton } from '../components';

/**
 * BlockRenderer
 * -------------
 * Renders a sorted list of CMS blocks. Public-mode is chrome-free; edit-mode
 * wraps each block with an EditableSection toolbar and adds AddBlockButtons
 * between sections.
 *
 * Props:
 *   blocks: Array<{
 *     block_uid: string,
 *     block_type: string,
 *     sort_order: number,
 *     is_visible?: boolean,
 *     data: object
 *   }>
 *   globals: { navbar?: {data}, footer?: {data} } | null
 *   editing: boolean
 *   withGlobals?: boolean   - render NavbarBlock at top + FooterBlock at bottom
 *   onChangeBlock?: (block_uid, newData) => void
 *   onReorderBlocks?: (orderedUids: string[]) => void
 *   onAddBlock?: (block_type: string, atIndex: number) => void
 *   onDeleteBlock?: (block_uid) => void
 *   onDuplicateBlock?: (block_uid) => void
 *   onToggleVisibility?: (block_uid) => void
 */
function BlockRenderer({
  blocks = [],
  globals = null,
  editing = false,
  withGlobals = false,
  onChangeBlock,
  onReorderBlocks,
  onAddBlock,
  onDeleteBlock,
  onDuplicateBlock,
  onToggleVisibility,
}) {
  const sorted = useMemo(
    () => [...(blocks || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [blocks]
  );

  const visible = editing ? sorted : sorted.filter((b) => b.is_visible !== false);
  const availableTypes = useMemo(
    () => Object.keys(registry).filter((k) => k !== 'navbar' && k !== 'footer'),
    []
  );

  const moveBlock = (uid, dir) => {
    if (!onReorderBlocks) return;
    const idx = sorted.findIndex((b) => b.block_uid === uid);
    if (idx < 0) return;
    const swap = idx + dir;
    if (swap < 0 || swap >= sorted.length) return;
    const next = [...sorted];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onReorderBlocks(next.map((b) => b.block_uid));
  };

  const NavbarCmp = getBlockComponent('navbar');
  const FooterCmp = getBlockComponent('footer');

  return (
    <>
      {withGlobals && globals?.navbar && (
        <NavbarCmp
          data={globals.navbar.data || globals.navbar}
          editing={editing}
          onChange={(d) => onChangeBlock && onChangeBlock('__navbar__', d)}
        />
      )}

      {visible.map((block) => {
        const Cmp = getBlockComponent(block.block_type);
        const idxInSorted = sorted.findIndex((b) => b.block_uid === block.block_uid);
        const node = (
          <Cmp
            data={block.data || {}}
            editing={editing}
            blockType={block.block_type}
            onChange={(d) => onChangeBlock && onChangeBlock(block.block_uid, d)}
          />
        );

        if (!editing) {
          return <React.Fragment key={block.block_uid}>{node}</React.Fragment>;
        }

        return (
          <React.Fragment key={block.block_uid}>
            <EditableSection
              blockUid={block.block_uid}
              blockType={block.block_type}
              isVisible={block.is_visible !== false}
              onMoveUp={idxInSorted > 0 ? () => moveBlock(block.block_uid, -1) : undefined}
              onMoveDown={idxInSorted < sorted.length - 1 ? () => moveBlock(block.block_uid, +1) : undefined}
              onDuplicate={onDuplicateBlock ? () => onDuplicateBlock(block.block_uid) : undefined}
              onDelete={onDeleteBlock ? () => onDeleteBlock(block.block_uid) : undefined}
              onToggleVisibility={onToggleVisibility ? () => onToggleVisibility(block.block_uid) : undefined}
            >
              {node}
            </EditableSection>
          </React.Fragment>
        );
      })}

      {editing && (
        <AddBlockButton
          availableTypes={availableTypes}
          onAdd={(t) => onAddBlock && onAddBlock(t, sorted.length)}
        />
      )}

      {withGlobals && globals?.footer && (
        <FooterCmp
          data={globals.footer.data || globals.footer}
          editing={editing}
          onChange={(d) => onChangeBlock && onChangeBlock('__footer__', d)}
        />
      )}
    </>
  );
}

export default BlockRenderer;
